import { UPLOAD_TOAST_ID } from '@/components/Ingest/FileUploadToastContent';
import { FileUploadToastContent } from '@/components/Ingest/FileUploadToastContent';
import { useUpgrade } from '@/components/Modals/UpgradeModal';
import { useAuth } from '@/context/AuthContext';
import useDeleteChatbot from '@/hooks/useDeleteChatbot';
import useLimits from '@/hooks/useLimits';
import useAnalytics from '@/lib/analytics/useAnalytics';
import { api } from '@/lib/trpc/react';
import {
  type UploadingFile,
  addUploadingFilesAtom,
} from '@/store/fileUploadStore';
import { updateUploadingFileAtom } from '@/store/fileUploadStore';
import { handlePdfUrlChecks } from '@/utils/convertPdfUrl';
import { sanitizeName, wait } from '@/utils/utils';
import { isIgnoredDomain } from '@unriddle/common';
import { validateWebsiteUrl } from '@unriddle/validators/url';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { toast } from 'sonner';
import { useCreateChatbot } from './useCreateChatbot';
import useIngestFile from './useIngestFile';
import { useIngestMetadata } from './useIngestMetadata';

export const useIngest = () => {
  const { showUpgrade } = useUpgrade();
  const utils = api.useUtils();
  const { track } = useAnalytics();
  const { exceededLimits, optimisticIncreaseImportLimit } = useLimits();
  const { user } = useAuth();
  const router = useRouter();
  const [isGenerating, setIsGenerating] = useState(false);
  const { ingestFile } = useIngestFile();
  const addFilesToUpload = useSetAtom(addUploadingFilesAtom);
  const updateFileStatus = useSetAtom(updateUploadingFileAtom);

  const { createChatbot, isPending, error: chatError } = useCreateChatbot();

  const {
    mutateAsync: ingestYtVideo,
    isPending: isYTIngestLoading,
    error: yTError,
  } = api.file.ingestYoutubeVideo.useMutation({
    onSuccess: () => {
      utils.user.limits.invalidate();
    },
  });

  const {
    mutateAsync: ingestWebsite,
    error: websiteIngestError,
    isPending: isWebsiteIngestLoading,
  } = api.ingest.ingestWebsite.useMutation({
    onSuccess: () => {
      utils.user.limits.invalidate();
    },
    onError: (_) => {
      toast.error('Failed to ingest website.');
    },
  });

  const { mutateDelete } = useDeleteChatbot();

  const onSubmitYTVideo = async ({ url }: { url: string }) => {
    if (exceededLimits?.isImportLimitExceeded) {
      showUpgrade('importLimit');
      track('importLimitHit', {
        userId: user?.id,
      });

      track('Paywall Shown', { reason: 'Import Limit Hit' });
      return;
    }

    const addedFilesState = addFilesToUpload([{ name: 'New Video' }]);
    openToast();

    const chatbot = await createChatbot({
      name: 'New Video',
      type: 'VIDEO',
      url,
    });
    if (!chatbot) return;
    optimisticIncreaseImportLimit();

    if (!chatbot) {
      updateFileStatus({
        id: addedFilesState[0].id,
        status: 'FAILED',
        errorMessage: 'Failed to create chatbot.',
      });
      return;
    }

    updateFileStatus({
      id: addedFilesState[0].id,
      status: 'IN_PROGRESS',
      chatbotId: chatbot.id,
      type: 'VIDEO',
    });

    ingestYtVideo({
      chatbotId: chatbot.id,
      url,
    });

    router.push(`/b/${chatbot.id}`);
  };

  const { ingestGenerateMetadata } = useIngestMetadata();

  const { mutate: ingestPdfUrl, isPending: isPdfIngestLoading } =
    api.file.uploadUrl.useMutation({
      onError: async (_, vars) => {
        mutateDelete([{ id: vars.chatbotId }]);
        if (vars.addedFileId) {
          updateFileStatus({
            id: vars.addedFileId,
            status: 'FAILED',
            errorMessage: 'File could not be processed.',
          });
        }
        toast.error('Add to library failed.');

        await wait(1000);
        toast.info(
          'Please download the pdf and add it to your library manually.',
        );
      },
      onSuccess: (_, vars) => {
        utils.user.limits.invalidate();
        ingestGenerateMetadata(vars.chatbotId, vars.name);
      },
    });

  const processPdfUrl = async (
    url: string,
    groupId?: string,
    addedFile?: UploadingFile,
  ) => {
    const segments = url.split('/');
    const urlFilename = segments[segments.length - 1];
    const filename = sanitizeName(urlFilename);

    const fileToTrack = addedFile ?? addFilesToUpload([{ name: filename }])[0];
    if (!addedFile) {
      openToast();
    }

    const chatbot = await createChatbot({
      name: filename,
      type: 'DOCUMENT',
      groupId,
    });
    if (!chatbot) return;

    optimisticIncreaseImportLimit();

    if (!chatbot) {
      updateFileStatus({
        id: fileToTrack.id,
        status: 'FAILED',
        errorMessage: 'Failed to create chatbot.',
      });
      return undefined;
    }

    updateFileStatus({
      id: fileToTrack.id,
      status: 'IN_PROGRESS',
      chatbotId: chatbot.id,
      type: 'DOCUMENT',
    });

    ingestPdfUrl({
      name: filename,
      chatbotId: chatbot.id,
      url,
      type: 'DOCUMENT',
      addedFileId: fileToTrack.id,
    });

    return chatbot.id;
  };

  const processUrl = async (url: string, groupId?: string) => {
    const addedFilesState = addFilesToUpload([{ name: 'Website' }]);
    openToast();

    const chatbot = await createChatbot({
      name: 'Website',
      type: 'WEBSITE',
      url,
      groupId,
    });
    if (!chatbot) return;

    optimisticIncreaseImportLimit();

    if (!chatbot) {
      updateFileStatus({
        id: addedFilesState[0].id,
        status: 'FAILED',
        errorMessage: 'Failed to create chatbot.',
      });
      return undefined;
    }

    updateFileStatus({
      id: addedFilesState[0].id,
      status: 'IN_PROGRESS',
      chatbotId: chatbot.id,
      type: 'WEBSITE',
    });

    ingestWebsite(
      {
        chatbotId: chatbot.id,
        url,
      },
      {},
    );

    return chatbot.id;
  };

  const openToast = () => {
    toast.custom((t) => <FileUploadToastContent toastId={t} />, {
      id: UPLOAD_TOAST_ID,
      duration: Number.POSITIVE_INFINITY,
      dismissible: false,
      position: 'bottom-left',
      className: '!px-0 !py-0 bg-popover border-border !rounded-xl',
    });
  };

  const onWebsiteUrlSubmit = async ({
    url,
    groupId,
    fileName,
  }: {
    url: string;
    groupId?: string;
    fileName?: string;
  }) => {
    if (!validateWebsiteUrl(url).valid) {
      return;
    }

    if (exceededLimits?.isImportLimitExceeded) {
      showUpgrade('importLimit');
      track('importLimitHit', {
        userId: user?.id,
      });

      track('Paywall Shown', { reason: 'Import Limit Hit' });
      return;
    }

    if (isIgnoredDomain(url)) {
      toast.error(
        'Due to copyright reasons we are not able to process the website.',
      );
      setIsGenerating(false);
      return;
    }
    setIsGenerating(true);
    let chatbotId: string | undefined;
    const {
      isPdf,
      url: checkedUrl,
      pdfData,
      contentType,
    } = await handlePdfUrlChecks(url);

    if (isPdf) {
      if (pdfData) {
        // If we have direct PDF data (from DOI/PMID endpoints)
        let addedFilesState: UploadingFile[] = [];
        try {
          // Create a file from the PDF data
          const segments = url.split('/');
          const urlFilename = segments[segments.length - 1];
          const filename = sanitizeName(urlFilename || 'paper.pdf');
          const file = new File([pdfData], fileName || filename, {
            type: contentType || 'application/pdf',
          });
          addedFilesState = addFilesToUpload([{ name: file.name }]);
          openToast();

          // Use the file ingest flow with the already imported hook
          const result = await ingestFile(file, groupId);
          if (result) {
            chatbotId = result;
            updateFileStatus({
              id: addedFilesState[0].id,
              status: 'IN_PROGRESS',
              chatbotId: chatbotId,
            });
          } else {
            updateFileStatus({
              id: addedFilesState[0].id,
              status: 'FAILED',
              errorMessage: 'File could not be processed.',
            });
            setIsGenerating(false);
            return;
          }
          setIsGenerating(false);
        } catch (error) {
          console.error('Error ingesting PDF data:', error);
          toast.error('Failed to process the PDF from DOI/PMID');
          setIsGenerating(false);
          if (addedFilesState.length > 0) {
            updateFileStatus({
              id: addedFilesState[0].id,
              status: 'FAILED',
              errorMessage: 'File could not be processed.',
            });
          }
          return;
        }
      } else {
        // Process PDF URL as before
        let addedFilesState: UploadingFile[] = [];
        if (fileName) {
          addedFilesState = addFilesToUpload([{ name: fileName }]);
          openToast();
        }
        chatbotId = await processPdfUrl(
          checkedUrl,
          groupId,
          addedFilesState.length > 0 ? addedFilesState[0] : undefined,
        );
        if (addedFilesState.length > 0) {
          if (chatbotId) {
            updateFileStatus({
              id: addedFilesState[0].id,
              status: 'IN_PROGRESS',
              chatbotId: chatbotId,
            });
          } else {
            updateFileStatus({
              id: addedFilesState[0].id,
              status: 'FAILED',
              errorMessage: 'File could not be processed.',
            });
          }
        }
      }
    } else {
      let addedFilesState: UploadingFile[] = [];
      if (fileName) {
        addedFilesState = addFilesToUpload([{ name: fileName }]);
        openToast();
      }
      chatbotId = await processUrl(url, groupId);
      if (addedFilesState.length > 0) {
        if (chatbotId) {
          updateFileStatus({
            id: addedFilesState[0].id,
            status: 'IN_PROGRESS',
            chatbotId: chatbotId,
          });
          track('chatbotCreated', {
            type: 'WEBSITE',
            chatbotId,
          });
        } else {
          updateFileStatus({
            id: addedFilesState[0].id,
            status: 'FAILED',
            errorMessage: 'File could not be processed.',
          });
        }
      }
      setIsGenerating(false);
    }

    if (chatbotId) {
      track('websiteIngested');
      router.push(`/b/${chatbotId}`);
    }
  };

  return {
    isChatCreationLoading: isPending,
    isYTIngestLoading,
    onSubmitYTVideo,
    isWebsiteIngestLoading,
    isPdfIngestLoading,
    isGenerating,
    chatError,
    yTError,
    websiteIngestError,
    onWebsiteUrlSubmit,
  };
};
